import { signalStore, withState, withMethods, withHooks, withComputed, patchState } from '@ngrx/signals';
import { computed, inject, effect } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AuthService,
  type SecurityKeyLoginCompletion,
  type User
} from '../core/services/auth.service';
import { LoggerService } from '../core/services/logger.service';
import { getUnmappedRoles } from '../core/constants/permissions';
import { sanitizeUrl } from '../core/utils/url-sanitizer';

// Re-export User type for other components
export type { User } from '../core/services/auth.service';

/**
 * Create an error representation that preserves HTTP status for proper error mapping.
 * The AuthErrorService needs the original error (especially HttpErrorResponse) to
 * properly map HTTP status codes to user-friendly messages.
 *
 * MEDIUM-5 FIX (DEV-1438): Prevents exposing internal error details to users while
 * still allowing AuthErrorService to determine the correct error type.
 *
 * @param error - The caught error object
 * @param defaultMessage - Default message if error is empty
 * @param logger - Optional logger service for warnings
 * @returns The original error for AuthErrorService to parse, or sanitized message for sensitive content
 */
function processAuthError(error: unknown, defaultMessage: string, logger?: LoggerService): unknown {
  if (!error) {
    return defaultMessage;
  }

  // If it's an HttpErrorResponse, return it as-is so AuthErrorService can parse it properly
  // AuthErrorService will map HTTP status codes (401, 403, 429, 500+) to user-friendly messages
  if (error instanceof HttpErrorResponse) {
    // Check if the error body contains sensitive information that needs sanitization
    const body = error.error;
    const rawMessage = typeof body === 'string'
      ? body
      : (body?.message || body?.error || '');

    // MEDIUM-5 FIX: Patterns that indicate sensitive internal errors
    const sensitivePatterns = [
      /sql|query|database|postgres|mysql|constraint|duplicate key|foreign key|unique violation/i,
      /stack trace|at \w+\.\w+/i,
      /null|undefined|NaN/i,
    ];

    for (const pattern of sensitivePatterns) {
      if (pattern.test(rawMessage)) {
        logger?.warn('[AuthStore] Sanitized sensitive error in HTTP response', { status: error.status });
        // Return a sanitized HttpErrorResponse-like object that AuthErrorService can still parse by status
        return new HttpErrorResponse({
          error: { message: 'An unexpected error occurred' },
          status: error.status,
          statusText: error.statusText,
          // sanitize: this HttpErrorResponse propagates to error pipeline + Sentry
          url: error.url ? sanitizeUrl(error.url) : undefined
        });
      }
    }

    // Return the original HttpErrorResponse - AuthErrorService will handle message mapping
    return error;
  }

  // For non-HTTP errors, extract and sanitize the message
  const err = error as Record<string, unknown>;
  const errorObj = err['error'] as Record<string, unknown> | undefined;
  const rawMessage =
    (errorObj?.['error'] as string) ||
    (errorObj?.['message'] as string) ||
    (err['message'] as string) ||
    '';

  // MEDIUM-5 FIX: Sanitize to prevent information leakage
  const sensitivePatterns = [
    { pattern: /sql|query|database|postgres|mysql|constraint|duplicate key|foreign key|unique violation/i, replacement: 'A server error occurred' },
    { pattern: /timeout|connection refused|ECONNREFUSED/i, replacement: 'Service temporarily unavailable' },
    { pattern: /stack trace|at \w+\.\w+/i, replacement: 'An unexpected error occurred' },
    { pattern: /internal server error/i, replacement: 'A server error occurred' },
    { pattern: /null|undefined|NaN/i, replacement: 'An unexpected error occurred' },
    { pattern: /token.*invalid|jwt.*error/i, replacement: 'Session expired. Please log in again.' },
    { pattern: /rate.*limit/i, replacement: 'Too many requests. Please try again later.' },
  ];

  for (const { pattern, replacement } of sensitivePatterns) {
    if (pattern.test(rawMessage)) {
      logger?.warn('[AuthStore] Sanitized sensitive error message', { pattern: pattern.source });
      return replacement;
    }
  }

  // Return the original error - AuthErrorService can still attempt to parse it
  return error;
}

// Additional interfaces needed by mock interceptor
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType?: string;
}

// Login credentials interface
export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
  /**
   * GAP-004: TOTP / backup code used to complete an MFA-gated login. When the
   * backend returns 401 MFA_REQUIRED, the login component resubmits the same
   * credentials with this code set.
   */
  mfaCode?: string;
  securityKey?: SecurityKeyLoginCompletion;
}

// Registration credentials interface
export interface RegisterCredentials {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// Auth state interface
// Error can be a string message or the original error object (e.g., HttpErrorResponse)
// for AuthErrorService to properly parse and map to user-friendly messages
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: unknown;
  rememberMe: boolean;
}

/**
 * AuthStore - Signal-based store that integrates with secure AuthService
 * 
 * This store provides reactive state management while delegating 
 * actual authentication operations to the secure AuthService.
 * No sensitive token data is stored in this store.
 */
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState({
    user: null as User | null,
    isAuthenticated: false,
    loading: false,
    error: null as unknown,
    rememberMe: false
  }),
  withComputed((store) => ({
    isLoggedIn: computed(() => store.isAuthenticated()),
    currentUser: computed(() => store.user()),
    userRoles: computed(() => store.user()?.roles || []),
    // Authorization is server-derived. Role-to-permission maps remain useful
    // for administration screens, but must never widen live UI capabilities.
    userPermissions: computed(() => [...new Set(store.user()?.permissions || [])]),
    // Extract a string error message from the unknown error type
    // This provides backward compatibility for components expecting string | null
    errorMessage: computed(() => {
      const err = store.error();
      if (!err) return null;
      if (typeof err === 'string') return err;
      if (err instanceof Error) return err.message;
      // Handle HttpErrorResponse or other error objects with message property
      if (typeof err === 'object' && 'message' in err) {
        return String((err as { message: unknown }).message);
      }
      return String(err);
    })
  })),
  withMethods((store) => {
    const authService = inject(AuthService);
    const logger = inject(LoggerService);

    return {
      login: async (credentials: LoginCredentials) => {
        patchState(store, { loading: true, error: null });

        try {
          logger.debug('[AuthStore] Starting login for:', { email: credentials.email });

          const response = await firstValueFrom(authService.login({
            email: credentials.email,
            password: credentials.password,
            rememberMe: credentials.rememberMe,
            // GAP-004: forward MFA code so an MFA-gated account can complete login
            mfaCode: credentials.mfaCode,
            securityKey: credentials.securityKey
          }));

          logger.debug('[AuthStore] Login response received:', { userId: response.user.id, email: response.user.email });

          // TENANT-CONTEXT FIX (Sentry ADMIN-INTERFACE-86/87): do NOT hydrate
          // effective-permissions here. The tenant.interceptor stamps X-Tenant-Id
          // from `tenantStore.currentTenant() ?? authStore.user().tenantId` and
          // REJECTS any /api/v1 call that has neither with a synthetic 400
          // ("No tenant context available"). On a fresh login neither is available
          // at this point: no tenant is selected yet, and `store.user` is patched
          // only AFTER this block (its mirror effect also flushes asynchronously),
          // so the interceptor reads a null user here. Firing effective-permissions
          // now is therefore GUARANTEED to 400 → empty roles/permissions →
          // spurious "permission denied" on every guarded route and a hung /config.
          //
          // Hydration is owned by the tenant-aware path instead: the protected
          // route's canActivate chain runs `tenantGuard` (which establishes the
          // tenant via switchTenant → loadEffectivePermissions WITH the correct
          // X-Tenant-Id) BEFORE roleGuard/permissionGuard, and those guards await
          // AuthService.authReady (toggled by loadEffectivePermissions) so they
          // never evaluate against a pre-hydration empty set. The login component's
          // handleSuccessfulLogin likewise establishes + hydrates the tenant before
          // it navigates. See tenant.store.ts switchTenant()/establishTenantContext().

          // Get user from auth service.
          const user = authService.currentUser();
          logger.debug('[AuthStore] Current user from service:', { email: user?.email });

          patchState(store, {
            user,
            isAuthenticated: true,
            loading: false,
            error: null,
            rememberMe: credentials.rememberMe || false
          });

          logger.info('[AuthStore] Login completed successfully', { isAuthenticated: store.isAuthenticated() });
        } catch (error: unknown) {
          logger.error('[AuthStore] Login error:', { error });
          // Process error - preserves HttpErrorResponse for AuthErrorService to parse
          // while sanitizing any sensitive internal error messages
          const processedError = processAuthError(error, 'Login failed', logger);
          patchState(store, {
            loading: false,
            error: processedError
          });
          throw error;
        }
      },

      register: async (credentials: RegisterCredentials) => {
        patchState(store, { loading: true, error: null });

        try {
          await firstValueFrom(authService.register(credentials));

          // TENANT-CONTEXT FIX (Sentry ADMIN-INTERFACE-86/87): do NOT hydrate
          // effective-permissions here — there is no tenant context yet, so the
          // tenant.interceptor would 400 the call (see the login() handler above
          // for the full rationale). Hydration is owned by the tenant-aware path:
          // tenantGuard (canActivate) establishes the tenant via switchTenant and
          // hydrates permissions WITH the correct X-Tenant-Id before any guarded
          // route activates; roleGuard/permissionGuard await AuthService.authReady.

          // Get user from auth service.
          const user = authService.currentUser();
          patchState(store, {
            user,
            isAuthenticated: true,
            loading: false,
            error: null
          });
        } catch (error: unknown) {
          // Process error - preserves HttpErrorResponse for AuthErrorService to parse
          const processedError = processAuthError(error, 'Registration failed', logger);
          patchState(store, {
            loading: false,
            error: processedError
          });
          throw error;
        }
      },

      logout: () => {
        authService.logout();
        // Clear store state
        patchState(store, {
          user: null,
          isAuthenticated: false,
          loading: false,
          error: null,
          rememberMe: false
        });
      },

      requestPasswordReset: async (email: string) => {
        patchState(store, { loading: true, error: null });

        try {
          await firstValueFrom(authService.resetPassword(email));
          patchState(store, { loading: false, error: null });
        } catch (error: unknown) {
          logger.error('[AuthStore] Password reset request error:', { error });
          // Process error - preserves HttpErrorResponse for AuthErrorService to parse
          const processedError = processAuthError(error, 'Password reset request failed', logger);
          patchState(store, {
            loading: false,
            error: processedError
          });
          throw error;
        }
      },

      confirmPasswordReset: async (token: string, newPassword: string) => {
        patchState(store, { loading: true, error: null });

        try {
          await firstValueFrom(authService.confirmPasswordReset(token, newPassword));
          patchState(store, { loading: false, error: null });
        } catch (error: unknown) {
          logger.error('[AuthStore] Password reset confirmation error:', { error });
          // Process error - preserves HttpErrorResponse for AuthErrorService to parse
          const processedError = processAuthError(error, 'Password reset confirmation failed', logger);
          patchState(store, {
            loading: false,
            error: processedError
          });
          throw error;
        }
      },

      // Social login removed - platform uses email/password authentication only
    hasRole: () => (role: string): boolean => {
      const roles = store.userRoles();
      return roles.includes(role);
    },
    hasAnyRole: () => (roles: string[]): boolean => {
      const userRoles = store.userRoles();
      return roles.some(role => userRoles.includes(role));
    },
    hasPermission: () => (permission: string): boolean => {
      const permissions = store.userPermissions();
      return permissions.includes(permission);
    },
    hasAnyPermission: () => (permissions: string[]): boolean => {
      const userPermissions = store.userPermissions();
      return permissions.some(permission => userPermissions.includes(permission));
    },
      setLoading: (loading: boolean) => {
        patchState(store, { loading });
      },

      setError: (error: unknown) => {
        patchState(store, { error });
      },
      
      setRememberMe: (rememberMe: boolean) => {
        patchState(store, { rememberMe });
      },
      
      syncWithAuthService: () => {
        // Sync store state with auth service
        const user = authService.currentUser();
        const isAuthenticated = authService.isAuthenticated();
        
        patchState(store, {
          user,
          isAuthenticated
        });
      },
      
      checkAuthentication: () => authService.isAuthenticated()
    };
  }),
  withHooks({
    onInit(store) {
      const authService = inject(AuthService);
      const logger = inject(LoggerService);
      logger.info('Auth store initialized');

      // Initial sync with auth service
      store.syncWithAuthService();

      // CRITICAL FIX: Reactive sync using effect()
      // This ensures AuthStore updates whenever AuthService signals change
      // Fixes session persistence on page reload - AuthService.initializeSession()
      // sets isAuthenticated=true after APP_INITIALIZER completes, but AuthStore
      // was not being notified of this change.
      let lastLoggedUnmapped: string | null = null;
      effect(() => {
        const isAuthenticated = authService.isAuthenticated();
        const user = authService.currentUser();

        // Only update if different to prevent infinite loops
        if (store.isAuthenticated() !== isAuthenticated || store.user() !== user) {
          patchState(store, { isAuthenticated, user });
          logger.debug('[AuthStore] Synced with AuthService via effect', { isAuthenticated, hasUser: !!user });

          // FE-10: Detect backend roles with no frontend permission mapping.
          // Deduplicate to avoid repeated warnings on token refresh cycles.
          // Reset dedup on logout so warnings fire again for the next session.
          if (!user) {
            lastLoggedUnmapped = null;
          } else if (user.roles?.length) {
            const unmapped = getUnmappedRoles(user.roles);
            const key = unmapped.sort().join(',');
            if (unmapped.length > 0 && key !== lastLoggedUnmapped) {
              lastLoggedUnmapped = key;
              logger.warn('[AuthStore] Role(s) have no frontend permission mapping', {
                component: 'AuthStore',
                unmappedRoles: unmapped,
              });
            }
          }
        }
      });
    }
  })
);
